/**
 * RFC 9457 problem details, thrown for every non-2xx response and for network failures.
 *
 * @typedef {{ type?: string, title?: string, status?: number, detail?: string, errors?: Record<string, string[]> } & Record<string, any>} ProblemDetails
 */

export const PROBLEM_TYPE_TWO_FACTOR_REQUIRED = 'two-factor-required';

const DEFAULT_TITLES = {
  0: 'Keine Verbindung zum Server',
  400: 'Ungültige Anfrage',
  401: 'Nicht angemeldet',
  403: 'Keine Berechtigung',
  404: 'Nicht gefunden',
  419: 'Sitzung abgelaufen',
  422: 'Die Eingaben sind ungültig',
  423: 'Passwortbestätigung erforderlich',
  429: 'Zu viele Anfragen – bitte kurz warten',
  500: 'Serverfehler',
  503: 'Dienst vorübergehend nicht verfügbar',
};

export class ProblemError extends Error {
  /**
   * @param {ProblemDetails} problem
   * @param {{ response?: Response, cause?: unknown }} [extra]
   */
  constructor(problem, extra = {}) {
    const status = problem.status ?? 0;
    const title = problem.title || DEFAULT_TITLES[status] || 'Unbekannter Fehler';
    super(problem.detail || title, extra.cause ? { cause: extra.cause } : undefined);
    this.name = 'ProblemError';
    this.status = status;
    this.type = problem.type || 'about:blank';
    this.title = title;
    this.detail = problem.detail ?? null;
    /** @type {Record<string, string[]>} */
    this.errors = problem.errors ?? {};
    this.problem = problem;
    this.response = extra.response;
  }

  get isNetworkError() {
    return this.status === 0;
  }
  get isUnauthenticated() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isCsrfMismatch() {
    return this.status === 419;
  }
  get isValidation() {
    return this.status === 422;
  }
  get isPasswordConfirmationRequired() {
    return this.status === 423;
  }
  get isThrottled() {
    return this.status === 429;
  }
  get isTwoFactorRequired() {
    return this.status === 403 && typeof this.type === 'string' && this.type.endsWith(PROBLEM_TYPE_TWO_FACTOR_REQUIRED);
  }

  /** First message per field, convenient for form libraries. */
  get fieldErrors() {
    return Object.fromEntries(Object.entries(this.errors).map(([field, messages]) => [field, Array.isArray(messages) ? messages[0] : String(messages)]));
  }
}

/** @param {unknown} error */
export function isProblemError(error) {
  return error instanceof ProblemError;
}

/**
 * Human readable message for toasts / alerts.
 * @param {unknown} error
 * @param {string} [fallback]
 */
export function getErrorMessage(error, fallback = 'Es ist ein Fehler aufgetreten.') {
  if (error instanceof ProblemError) return error.detail || error.title || fallback;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Does the error carry the given RFC 9457 problem type? The backend sends `type` as
 * `<base URL>/<slug>` (e.g. `…/already-sent`) – only the slug is compared.
 * @param {unknown} error
 * @param {string|string[]} slugs
 */
export function hasProblemType(error, slugs) {
  const type = /** @type {any} */ (error)?.type;
  if (typeof type !== 'string') return false;
  const list = Array.isArray(slugs) ? slugs : [slugs];
  return list.some((slug) => type === slug || type.endsWith(`/${slug}`));
}

/** Problem types that mean "already sent – repeat only with `resend: true`". */
export const RESEND_PROBLEM_TYPES = ['already-sent', 'already-dispatched'];
